import styled, {keyframes} from 'styled-components'
import {RiErrorWarningLine, RiTimeLine, RiCheckLine} from '@remixicon/react'

import {MainButton} from '@festi/common/components/buttons'
import {H3, P} from '@festi/common/components/typography'
import {styledTheme} from '@festi/common/themes'
import {formatPrice} from '@festi/utils/numbers'

const LoadingRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;
  width: 300px;
  padding: 24px;
  margin: auto;
  height: 400px;
`

const spin = keyframes`
  from {
      transform: rotate(0deg);
  }
  to {
      transform: rotate(360deg);
  }
`

const LoadingCircle = styled.img`
  animation: ${spin} 1s linear infinite;
  width: 60px;
  height: 60px;
`

const AlertCircle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${styledTheme.palette.pink};

  &.success {
    background-color: ${styledTheme.palette.green};
  }
`

const ContactLink = styled.a`
  color: ${styledTheme.palette.blue};
  text-decoration: underline;
  text-underline-offset: 1px;
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`

const AmountContainer = styled.div`
  width: fit-content;
  border-radius: 4px;
  padding: 4px;
  background-color: ${styledTheme.palette.green};
  rotate: -1deg;

  h3 {
    rotate: 1deg;
  }
`

interface CheckEligibilityProps {
  openRegistrationModal: () => void
}

export function CheckEligibility({
  openRegistrationModal,
}: CheckEligibilityProps) {
  return (
    <>
      <H3 center headline>
        Sveigjanlegir greiðslumöguleikar Elko
      </H3>
      {/* TODO: Change text according to only offering installments option */}
      <P center>
        Dreifðu greiðslunum eða borgaðu bara aðeins seinna &ndash; valið er
        þitt. Lánshæfismat er ókeypis, hratt og öruggt.
      </P>

      <MainButton
        label="Athuga lánshæfi"
        style={{paddingInline: '24px'}}
        onClick={openRegistrationModal}
      />
    </>
  )
}

interface BudgetTooLowProps {
  creditAmount: number
  purchaseAmount: number
}

export function BudgetTooLow({
  creditAmount,
  purchaseAmount,
}: BudgetTooLowProps) {
  return (
    <>
      <AlertCircle>
        <RiErrorWarningLine size={32} color={styledTheme.palette.white} />
      </AlertCircle>
      <H3 center headline>
        Snjallgreiðslu heimild of lág
      </H3>
      <div>
        <P center>
          Snjallgreiðslu heimild:{' '}
          <strong>{formatPrice(creditAmount)} kr.</strong>
        </P>
        <P center withGutter>
          Kaupverðmæti: <strong>{formatPrice(purchaseAmount)} kr.</strong>
        </P>
        {/* TODO: Better text? */}
        <P center>
          Þú getur prófað að fjarlægja eitthvað af hlutunum úr körfunni þinni
          eða endurnýjað heimildina á prófílnum þínum.
        </P>
      </div>
    </>
  )
}

export function PendingReview() {
  return (
    <>
      <AlertCircle>
        <RiTimeLine size={32} color={styledTheme.palette.white} />
      </AlertCircle>
      <ContentWrapper>
        <H3 center headline>
          Bíður yfirferðar
        </H3>
        <div>
          <P center>
            Teymið okkar þarf að fara yfir beiðni þína. Vinsamlegast gefið okkur
            1–2 virka daga til að svara þér.
          </P>
        </div>
      </ContentWrapper>
    </>
  )
}

export function NotEligible() {
  return (
    <>
      <AlertCircle>
        <RiErrorWarningLine size={32} color={styledTheme.palette.white} />
      </AlertCircle>
      <ContentWrapper>
        <H3 center headline>
          Við getum ekki boðið þér Snjallgreiðslur
        </H3>
        <P center>
          Ef þig vantar frekari upplýsingar um lánshæfismat Snjallgreiðslna,{' '}
          <ContactLink href="mailto:elko@elko.is">
            hafðu samband við þjónustuver okkar
          </ContactLink>
          .
        </P>
      </ContentWrapper>
    </>
  )
}

interface CircularLoaderProps {
  text: string
}

export function CircularLoader({text}: CircularLoaderProps) {
  return (
    <LoadingRoot>
      <LoadingCircle alt="Loading circle" src="/icons/loading-circle.svg" />
      <P center>{text}</P>
    </LoadingRoot>
  )
}

interface RegistrationSuccessProps {
  amount: number
}

export function RegistrationSuccess({amount}: RegistrationSuccessProps) {
  return (
    <>
      <AlertCircle className="success">
        <RiCheckLine size={32} color={styledTheme.palette.white} />
      </AlertCircle>
      <ContentWrapper>
        {amount != null && (
          <div>
            <AmountContainer>
              <H3 center headline>
                {formatPrice(amount)}
              </H3>
            </AmountContainer>
            <H3 center headline>
              samþykkt
            </H3>
          </div>
        )}
        <P center>Nú getur þú gengið frá kaupunum.</P>
      </ContentWrapper>
    </>
  )
}
